'use client';

import { useState, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Building2, Mail, Phone } from 'lucide-react';
import { DataTable } from '../../../components/shared/DataTable';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { suppliers, supplierGroups } from '../data/mock-data';
import { formatCurrency } from '../../../types';
import type { Supplier } from '../types';

// 공급업체 추가 모달
function SupplierAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="공급업체 추가"
      description="새로운 공급업체 정보를 입력합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>저장</Button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField label="업체코드" required>
          <FormInput placeholder="자동 생성" disabled />
        </FormField>
        <FormField label="업체명" required>
          <FormInput placeholder="업체명을 입력하세요" />
        </FormField>
        <FormField label="담당자">
          <FormInput placeholder="담당자명" />
        </FormField>
        <FormField label="이메일">
          <FormInput type="email" placeholder="example@email.com" />
        </FormField>
        <FormField label="전화번호">
          <FormInput placeholder="02-000-0000" />
        </FormField>
        <FormField label="공급업체 그룹" required>
          <FormSelect>
            <option value="">선택하세요</option>
            {supplierGroups.map((group) => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </FormSelect>
        </FormField>
        <FormField label="사업자등록번호">
          <FormInput placeholder="000-00-00000" />
        </FormField>
        <FormField label="결제조건">
          <FormSelect>
            <option value="">선택하세요</option>
            <option value="현금">현금</option>
            <option value="30일">30일</option>
            <option value="60일">60일</option>
            <option value="월말결제">월말결제</option>
          </FormSelect>
        </FormField>
        <div className="col-span-2">
          <FormField label="주소">
            <FormTextarea rows={2} placeholder="주소를 입력하세요" />
          </FormField>
        </div>
        <div className="col-span-2">
          <FormField label="비고">
            <FormTextarea rows={2} placeholder="추가 정보를 입력하세요" />
          </FormField>
        </div>
      </div>
    </Modal>
  );
}

export function SupplierListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('all');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return suppliers.filter((supplier) => {
      // 검색 필터
      const matchesSearch =
        supplier.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (supplier.contactPerson && supplier.contactPerson.toLowerCase().includes(searchQuery.toLowerCase()));

      // 그룹 필터
      const matchesGroup =
        selectedGroup === 'all' || supplier.supplierGroup === selectedGroup;

      // 활성 필터
      const matchesActive = !showActiveOnly || supplier.isActive;

      return matchesSearch && matchesGroup && matchesActive;
    });
  }, [searchQuery, selectedGroup, showActiveOnly]);

  const columns = [
    {
      key: 'code',
      label: '업체코드',
      width: '9%',
      sortable: true,
      render: (value: unknown) => (
        <span className="font-mono text-sm">{String(value)}</span>
      ),
    },
    {
      key: 'name',
      label: '업체명',
      width: '16%',
      sortable: true,
      render: (value: unknown, row: Supplier) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{String(value)}</div>
            {row.contactPerson && (
              <div className="text-xs text-muted-foreground truncate">
                담당: {row.contactPerson}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      label: '연락처',
      width: '16%',
      render: (_: unknown, row: Supplier) => (
        <div className="space-y-0.5">
          {row.email && (
            <div className="flex items-center gap-1 text-xs">
              <Mail className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="truncate">{row.email}</span>
            </div>
          )}
          {row.phone && (
            <div className="flex items-center gap-1 text-xs">
              <Phone className="w-3 h-3 text-muted-foreground flex-shrink-0" />
              <span className="whitespace-nowrap">{row.phone}</span>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'supplierGroup',
      label: '그룹',
      width: '8%',
      render: (value: unknown) => {
        const group = supplierGroups.find((g) => g.id === value);
        return <span className="whitespace-nowrap">{group?.name || String(value)}</span>;
      },
    },
    {
      key: 'paymentTerms',
      label: '결제조건',
      width: '10%',
      render: (value: unknown) => (
        <span className="text-sm whitespace-nowrap">{value ? String(value) : '-'}</span>
      ),
    },
    {
      key: 'totalPurchase',
      label: '총 구매금액',
      width: '12%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => (
        <span className="whitespace-nowrap">{formatCurrency(value as number)}</span>
      ),
    },
    {
      key: 'outstandingAmount',
      label: '미지급금',
      width: '11%',
      align: 'right' as const,
      sortable: true,
      render: (value: unknown) => {
        const amount = value as number;
        return (
          <span className={`whitespace-nowrap ${amount > 0 ? 'text-red-500 font-medium' : ''}`}>
            {formatCurrency(amount)}
          </span>
        );
      },
    },
    {
      key: 'isActive',
      label: '상태',
      width: '7%',
      render: (value: unknown) => (
        <span
          className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
            value
              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
          }`}
        >
          {value ? '활성' : '비활성'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      width: '7%',
      render: () => (
        <div className="flex items-center gap-1">
          <button className="p-1.5 rounded hover:bg-accent" title="수정">
            <Edit2 className="w-4 h-4 text-muted-foreground" />
          </button>
          <button className="p-1.5 rounded hover:bg-accent" title="삭제">
            <Trash2 className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>
      ),
    },
  ];

  // 요약 정보
  const totalSuppliers = suppliers.length;
  const activeSuppliers = suppliers.filter((s) => s.isActive).length;
  const totalOutstanding = suppliers.reduce((sum, s) => sum + s.outstandingAmount, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="공급업체 관리"
        description="공급업체 및 거래처 정보를 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            공급업체 추가
          </button>
        }
      />

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">전체 공급업체</div>
          <div className="text-2xl font-bold mt-1">
            {totalSuppliers}개
            <span className="text-sm font-normal text-muted-foreground ml-2">
              (활성 {activeSuppliers}개)
            </span>
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 미지급금</div>
          <div className="text-2xl font-bold mt-1 text-red-500">
            {formatCurrency(totalOutstanding)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">공급업체 그룹</div>
          <div className="text-2xl font-bold mt-1">
            {supplierGroups.length}개
          </div>
        </div>
      </div>

      {/* 필터 영역 */}
      <div className="flex flex-wrap gap-4">
        {/* 검색 */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="업체코드, 업체명, 담당자 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        {/* 그룹 필터 */}
        <select
          value={selectedGroup}
          onChange={(e) => setSelectedGroup(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 그룹</option>
          {supplierGroups.map((group) => (
            <option key={group.id} value={group.id}>
              {group.name} ({group.supplierCount})
            </option>
          ))}
        </select>

        {/* 활성 필터 */}
        <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-background text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showActiveOnly}
            onChange={(e) => setShowActiveOnly(e.target.checked)}
            className="rounded border-border"
          />
          <span>활성 업체만</span>
        </label>
      </div>

      {/* 데이터 테이블 */}
      <DataTable
        data={filteredData}
        columns={columns}
        pageSize={10}
        emptyMessage="검색 결과가 없습니다."
      />

      {/* 공급업체 추가 모달 */}
      <SupplierAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
